import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Col,
    Dropdown,
    DropdownButton,
    Form,
    Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    AuthedLayout,
    EditableTableDiv,
    HeaderDisplayKeysWithType,
} from '../../../components';
import { useClassGrade, useSemGrade } from '../../../hook';
import { StudentGradeNoSeat, StudentDiseaseBodyMind, UserInfo } from '../../../model';
import apis from '../../../apis';
import {
    downloadDataAsExcel,
    printDataAsTable,
    SheetHeaderDisplay,
} from '../../../utils';
import { toast } from 'react-toastify';
type PageData = StudentDiseaseBodyMind & StudentGradeNoSeat;

const tableHeader: (SheetHeaderDisplay<PageData> &
    HeaderDisplayKeysWithType<PageData>)[] = [
        { display: '年', property: 'grade' },
        {
            display: '班級',
            property: 'className',
        },
        { display: '座號', property: 'seat' },
        { display: '姓名', property: 'name' },
        { display: '疾病', property: 'diseaseName' },
        { display: '陳述', property: 'description' },
        { display: '照護', property: 'treatment' },
        { display: '護送醫院', property: 'hospital' },
        { display: '身心障礙手冊', property: 'bodyMindName' },
        { display: '重大傷病卡', property: 'seriousDiseaseName' },
    ];

const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const diseaseClassBodyMind: FunctionComponent<Props> = ({
    user,
    catchErrorForModal,
}) => {
    const { currentSemester = '', semesters = {} } = user || {};
    const [currentSem, setCurrentSem] = useState(currentSemester);
    const { year = -1, sem = -1 } = semesters[currentSem];
    const yearSem = { year: year, sem: sem };
    const { selectedClassGrade: classGrade, element: classGradeElement } =
        useClassGrade({
            yearSem
        });
    const [students, setStudents] = useState<PageData[]>([]);
    const [onlySchoolCreated, setOnlySchoolCreated] = useState<boolean>(true);
    const [userinfo, setUser] = useState<UserInfo>();

    const [isExporting, setIsExporting] = useState(false);
    useEffect(() => {
        if (yearSem && classGrade) {
            apis.getUserInfo()
                .then((u) => {
                    setUser(u);
                })
                .catch(catchErrorForModal);

            toast
                .promise(
                    apis.getClassDiseaseBodyMind(yearSem.year, yearSem.sem, onlySchoolCreated, classGrade.id)
                        .then((r) =>
                            setStudents(
                                r.map(({ studentDiseaseBodyMind, ...s }) => ({
                                    ...studentDiseaseBodyMind,
                                    ...s,
                                }))
                            )
                        ),
                    {
                        pending: '資料載入中......',
                        success: '資料載入成功！',
                        error: '資料載入失敗！請查看錯誤資訊。',
                    }
                )
        }
    }, [classGrade?.id, yearSem?.year, yearSem?.sem, onlySchoolCreated]);

    const title =
        `${user.schools[user.currentSchool].name}` +
        `${yearSem.year}學年` +
        `${yearSem.sem}學期_個人疾病史(護送醫院 身障手冊 重大傷病卡)_分班`;

    const footer =
        '承辦人:          組長:          主任:          校長:';

    const handleAllClassExport = async (
        type: 'excel' | 'print'
    ) => {
        if (!yearSem || isExporting) return;

        setIsExporting(true);

        const exportPromise = apis
            .getClassDiseaseBodyMind(
                yearSem.year,
                yearSem.sem,
                onlySchoolCreated
            )
            .then((r) => {
                const allStudents = r.map(
                    ({ studentDiseaseBodyMind, ...student }) => ({
                        ...studentDiseaseBodyMind,
                        ...student,
                    })
                );

                if (type === 'excel') {
                    downloadDataAsExcel({
                        title,
                        values: allStudents,
                        headers: tableHeader,
                        groupBy: (value) =>
                            `${value.grade}年${value.no}班`,
                        footer,
                        exportfile: userinfo?.exportFileType,
                    });

                    return;
                }

                printDataAsTable({
                    title,
                    values: allStudents,
                    headers: tableHeader,
                    groupBy: (value) =>
                        `${value.grade}年${value.no}班`,
                    footer,
                });
            });

        try {
            await toast.promise(exportPromise, {
                pending:
                    type === 'excel'
                        ? '所有班級資料整理中，請稍候......'
                        : '所有班級列印資料準備中，請稍候......',
                success:
                    type === 'excel'
                        ? 'Excel 資料準備完成！'
                        : '列印資料準備完成！',
                error: '資料處理失敗！請查看錯誤資訊。',
            });
        } catch (error) {
            catchErrorForModal(error);
        } finally {
            setIsExporting(false);
        }
    };

    const handleSingleClassExport = async (
        type: 'excel' | 'print'
    ) => {
        if (!students.length || isExporting) return;

        setIsExporting(true);

        const exportPromise = new Promise<void>(
            (resolve, reject) => {
                try {
                    window.setTimeout(() => {
                        if (type === 'excel') {
                            downloadDataAsExcel({
                                title,
                                values: students,
                                headers: tableHeader,
                                groupBy: () =>
                                    `${classGrade?.grade}年${classGrade?.no}班`,
                                footer,
                                exportfile: userinfo?.exportFileType,
                            });
                        } else {
                            printDataAsTable({
                                title,
                                values: students,
                                headers: tableHeader,
                                groupBy: () =>
                                    `${classGrade?.grade}年${classGrade?.no}班`,
                                footer,
                            });
                        }

                        resolve();
                    }, 100);
                } catch (error) {
                    reject(error);
                }
            }
        );

        try {
            await toast.promise(exportPromise, {
                pending:
                    type === 'excel'
                        ? 'Excel 資料整理中，請稍候......'
                        : '列印資料準備中，請稍候......',
                success:
                    type === 'excel'
                        ? 'Excel 資料準備完成！'
                        : '列印資料準備完成！',
                error: '資料處理失敗！請查看錯誤資訊。',
            });
        } catch (error) {
            catchErrorForModal(error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <AuthedLayout>
            <Row className="justify-content-between">
                <Col>
                    <Row>
                        <Col xs={3} className="mr-3">
                            {classGradeElement}
                        </Col>
                    </Row>
                </Col>
                <Col xs={3} className="mr-3">
                    <Form.Check
                        type="checkbox"
                        id="chk-only-school-created"
                        label="只顯示本校建置資料"
                        checked={onlySchoolCreated}
                        onChange={(e) => setOnlySchoolCreated(e.target.checked)}
                    />
                </Col>
                <div className="d-flex">
                    {/* 所有班級 */}
                    <DropdownButton
                        id="disease-bodymind-all-export"
                        title={
                            isExporting
                                ? '資料處理中...'
                                : '所有班級'
                        }
                        variant="info"
                        className="mr-3"
                        disabled={!yearSem || isExporting}
                    >
                        <Dropdown.Item
                            onClick={() =>
                                handleAllClassExport('excel')
                            }
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() =>
                                handleAllClassExport('print')
                            }
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>

                    {/* 單一班級 */}
                    <DropdownButton
                        id="disease-bodymind-single-export"
                        title={
                            isExporting
                                ? '資料處理中...'
                                : '單一班級'
                        }
                        variant="success"
                        disabled={
                            !students.length ||
                            isExporting
                        }
                    >
                        <Dropdown.Item
                            onClick={() =>
                                handleSingleClassExport('excel')
                            }
                        >
                            Excel 下載
                        </Dropdown.Item>

                        <Dropdown.Item
                            onClick={() =>
                                handleSingleClassExport('print')
                            }
                        >
                            列印
                        </Dropdown.Item>
                    </DropdownButton>
                </div>
            </Row>
            <hr />
            <Row>
                <EditableTableDiv
                    headers={tableHeader}
                    values={students}
                />
            </Row>
        </AuthedLayout>
    );
};

export const DiseaseClassBodyMind = connector(diseaseClassBodyMind);